import { useId, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { ticketStatusLabels } from '@brimo/shared';
import { Banner, Button, Icon, MenuItem, MenuList } from '../../../ds/components';
import { clsx } from '../../../ds/clsx';
import { ApiError, errorMessage } from '../../../lib/api';
import { sendTicketMessage } from '../api';
import type { StatusAfter, TicketDetail } from '../types';
import styles from './detail.module.css';

const MAX_FILES = 5;
const ACCEPT = 'application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png';
const statusChoices: readonly StatusAfter[] = ['in_progress', 'waiting', 'resolved'];

export interface ReplyBoxProps {
  ticketId: string;
  reply: NonNullable<TicketDetail['reply']>;
  onSent: (detail: TicketDetail) => void;
}

/**
 * The reply box of Backoffice 05/06/08: tabs „Antwort an Kunden" / „Interne Notiz", the text, „Anhang" (≤ 5 PDF/JPG/PNG),
 * „Status danach" (default from the API) and the split button „Senden ▾" whose menu sends with one of the three
 * statuses. `POST /tickets/:id/messages` as multipart with upload progress. A note never changes the status.
 */
export function ReplyBox({ ticketId, reply, onSent }: ReplyBoxProps) {
  const textId = useId();
  const fileInput = useRef<HTMLInputElement>(null);
  const [kind, setKind] = useState<'reply' | 'note'>('reply');
  const [text, setText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [statusAfter, setStatusAfter] = useState<StatusAfter>(reply.statusAfter);
  const [menuOpen, setMenuOpen] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pending = progress !== null;

  function addFiles(list: FileList | null) {
    if (!list) return;
    const next = [...files, ...Array.from(list)];
    if (next.length > MAX_FILES) setError(`Höchstens ${MAX_FILES} Anhänge je Nachricht.`);
    else setError(null);
    setFiles(next.slice(0, MAX_FILES));
  }

  async function send(status: StatusAfter) {
    setMenuOpen(false);
    if (!text.trim()) {
      setError('Bitte einen Text eingeben.');
      return;
    }
    setError(null);
    setProgress(0);
    try {
      const detail = await sendTicketMessage(
        ticketId,
        { kind, text: text.trim(), statusAfter: kind === 'reply' ? status : null, files },
        (fraction) => setProgress(Math.round(fraction * 100)),
      );
      setText('');
      setFiles([]);
      setStatusAfter(reply.statusAfter);
      onSent(detail);
    } catch (caught) {
      const fields = caught instanceof ApiError ? Object.values(caught.fields) : [];
      setError(fields[0] ?? errorMessage(caught));
    } finally {
      setProgress(null);
    }
  }

  return (
    <section className={styles.replyBox} aria-label="Antworten">
      <div className={styles.replyTabs} role="tablist" aria-label="Art der Nachricht">
        {(
          [
            ['reply', 'Antwort an Kunden'],
            ['note', 'Interne Notiz'],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={kind === value}
            aria-controls={textId}
            className={clsx(styles.replyTab, kind === value && styles.replyTabActive)}
            onClick={() => setKind(value)}
          >
            {label}
          </button>
        ))}
      </div>
      <textarea
        id={textId}
        className={clsx(styles.replyText, kind === 'note' && styles.replyTextNote)}
        aria-label={kind === 'reply' ? 'Antwort an Kunden' : 'Interne Notiz'}
        placeholder={kind === 'reply' ? reply.placeholder : 'Notiz für das Team …'}
        value={text}
        disabled={pending}
        onChange={(event) => setText(event.target.value)}
      />
      {files.length > 0 ? (
        <ul className={styles.replyFiles} aria-label="Anhänge">
          {files.map((file, index) => (
            <li key={`${file.name}-${index}`} className={styles.fileChip}>
              <span className={styles.fileExt} aria-hidden="true">
                {file.name.split('.').pop()}
              </span>
              {file.name}
              <button
                type="button"
                className={styles.fileRemove}
                aria-label={`${file.name} entfernen`}
                disabled={pending}
                onClick={() => setFiles(files.filter((_, position) => position !== index))}
              >
                <Icon icon={X} size={16} />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {error ? (
        <div className={styles.replyError}>
          <Banner tone="danger">{error}</Banner>
        </div>
      ) : null}
      <div className={styles.replyFoot}>
        <input
          ref={fileInput}
          type="file"
          multiple
          accept={ACCEPT}
          hidden
          onChange={(event) => {
            addFiles(event.target.files);
            event.target.value = '';
          }}
        />
        <Button
          variant="secondary"
          size="small"
          disabled={pending || files.length >= MAX_FILES}
          onClick={() => fileInput.current?.click()}
        >
          Anhang
        </Button>
        {pending ? (
          <span className={styles.progress} role="status">
            Wird gesendet · {progress} %
          </span>
        ) : null}
        {kind === 'reply' ? (
          <>
            <span className={styles.statusAfterLabel} id={`${textId}-after`}>
              Status danach
            </span>
            <span className={styles.statusAfterWrap}>
              <select
                className={styles.statusAfter}
                aria-labelledby={`${textId}-after`}
                value={statusAfter}
                disabled={pending}
                onChange={(event) => setStatusAfter(event.target.value as StatusAfter)}
              >
                {statusChoices.map((choice) => (
                  <option key={choice} value={choice}>
                    {ticketStatusLabels[choice]}
                  </option>
                ))}
              </select>
              <span className={styles.statusAfterCaret} aria-hidden="true">
                ▾
              </span>
            </span>
          </>
        ) : (
          <span className={styles.statusAfterLabel}>Nur für das Team sichtbar</span>
        )}
        {kind === 'reply' ? (
          <Button
            variant="split"
            loading={pending}
            chevronLabel="Senden mit Status"
            popoverOpen={menuOpen}
            onPopoverOpenChange={setMenuOpen}
            popoverWidth={220}
            popover={
              <MenuList label="Senden mit Status" density="compact" onClose={() => setMenuOpen(false)}>
                {statusChoices.map((choice) => (
                  <MenuItem
                    key={choice}
                    label={`Senden · ${ticketStatusLabels[choice]}`}
                    onSelect={() => void send(choice)}
                  />
                ))}
              </MenuList>
            }
            onClick={() => void send(statusAfter)}
          >
            Senden
          </Button>
        ) : (
          <Button loading={pending} onClick={() => void send(statusAfter)}>
            Senden
          </Button>
        )}
      </div>
    </section>
  );
}
